import React, { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight, Play } from "lucide-react";
import { BRAND, STATS } from "@/lib/brand";
import { MaskLines } from "@/components/Motion";
import { useStore } from "@/context/StoreContext";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.04, 1.16]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-16%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const { format, product } = useStore();
  const flagship = product("fight-studio");

  const rx = useSpring(useMotionValue(0), { stiffness: 120, damping: 18 });
  const ry = useSpring(useMotionValue(0), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 26);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 26);
  };
  const onLeave = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <section
      id="top"
      ref={ref}
      className="grain relative min-h-[100svh] overflow-hidden border-b border-white/8"
    >
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img src={BRAND.banner} alt="" className="h-full w-full object-cover opacity-85" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#08080a]/70 via-[#08080a]/40 to-[#08080a]" />
      <div className="hairline-grid absolute inset-0 opacity-60" />

      <motion.div
        style={{ y: contentY, opacity: fade }}
        className="relative mx-auto flex min-h-[100svh] max-w-[1400px] flex-col justify-end px-5 pb-16 pt-32 sm:px-8 lg:pb-24"
      >
        <div className="grid gap-14 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="mb-8 flex flex-wrap items-center gap-x-5 gap-y-2"
            >
              <span className="label">Orvyn FiveM Scripts</span>
              <span className="h-px w-10 bg-white/20" />
              <span className="label">Standalone</span>
              <span className="h-px w-10 bg-white/20" />
              <span className="label">Documented</span>
            </motion.div>

            <h1 className="font-display text-[clamp(2rem,4.3vw,3.8rem)] font-extrabold uppercase leading-[0.94] tracking-[-0.02em] text-white">
              <MaskLines
                lines={["Scripts for servers", "that care about", "the detail."]}
                lineClass="whitespace-nowrap"
                delay={0.35}
                stagger={0.11}
              />
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.95, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="mt-8 max-w-xl text-sm leading-relaxed text-white/55 sm:text-base"
            >
              Orvyn makes standalone FiveM scripts and assets — no framework to bridge, no database,
              proper documentation in the box and real support in Discord. Our first major release,
              Fight Studio, is out now with the Karate GI’s add-on alongside it.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="mt-11 flex flex-wrap items-center gap-4"
            >
              <a
                href="#store"
                data-testid="hero-store-button"
                className="group relative overflow-hidden bg-white px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-black"
              >
                <span className="relative z-10 flex items-center gap-3">
                  Browse the store
                  <ArrowDownRight className="h-4 w-4 transition-transform duration-400 group-hover:translate-x-1 group-hover:translate-y-1" />
                </span>
                <span className="absolute inset-0 z-0 -translate-y-full bg-[hsl(var(--orv-blue))] transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-y-0" />
              </a>
              <a
                href="#showcase"
                data-testid="hero-showcase-button"
                className="group flex items-center gap-3 border border-white/15 px-7 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-white/75 transition-colors duration-300 hover:border-white/45 hover:text-white"
              >
                <Play className="h-3.5 w-3.5 fill-current" />
                Watch it in action
              </a>
            </motion.div>

            <motion.a
              href="#store"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.25, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              data-testid="hero-flagship-strip"
              className="group mt-10 flex max-w-xl flex-wrap items-center gap-x-5 gap-y-2 border border-white/10 px-5 py-4 transition-colors duration-300 hover:border-white/30"
            >
              <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[hsl(var(--orv-blue))]">
                Out now
              </span>
              <span className="font-display text-sm font-bold uppercase tracking-[0.1em] text-white">
                Orvyn Fight Studio {flagship.version}
              </span>
              <span className="ml-auto font-mono text-[11px] tracking-[0.14em] text-white/55">
                {format(flagship.price)}
              </span>
              <ArrowDownRight className="h-3.5 w-3.5 text-white/40 transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1" />
            </motion.a>
          </div>

          <div className="lg:col-span-4">
            <motion.div
              onMouseMove={onMove}
              onMouseLeave={onLeave}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              style={{ perspective: 900 }}
              className="relative mx-auto hidden w-full max-w-[380px] lg:block"
            >
              <motion.div
                style={{ rotateX: rx, rotateY: ry }}
                className="relative grid place-items-center py-10"
              >
                <div className="absolute inset-x-4 aspect-square rounded-full border border-white/8" />
                <div className="absolute inset-x-4 aspect-square rounded-full border border-white/10 pulse-ring" />
                <div className="absolute inset-x-4 aspect-square rounded-full bg-[radial-gradient(circle,rgba(59,130,246,0.09),transparent_66%)]" />
                <img
                  src={BRAND.mark}
                  alt="Orvyn FiveM Scripts"
                  className="relative w-[46%] drop-shadow-[0_24px_60px_rgba(0,0,0,0.9)]"
                />
              </motion.div>
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.35, duration: 1 }}
          className="mt-14 grid grid-cols-2 gap-px border-t border-white/8 pt-px sm:grid-cols-4"
        >
          {STATS.map((s) => (
            <div key={s.label} className="px-1 pt-7">
              <div className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
                {s.value}
              </div>
              <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/55">
                {s.label}
              </div>
              <div className="mt-1.5 text-[11px] leading-snug text-white/25">{s.sub}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};
